import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { LocalStorageService } from 'ngx-webstorage';

import { AccountService } from '../core/auth/account.service';
import { Account } from '../core/user/account.model';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-home',
  standalone: false,
  templateUrl: './home.component.html',
  styleUrls: ['home.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  account: Account | null = null;
  greetingText = '';
  authSubscription?: Subscription;
  languageSubscription?: Subscription;
  private greetingIntervalId?: number;

  constructor(
    private accountService: AccountService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.authSubscription = this.accountService
      .getAuthenticationState()
      .subscribe((account) => {
        this.account = account;
        this.updateGreeting(account);
      });

    this.languageSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateGreeting();
    });

    this.accountService.identity().subscribe((res: Account | null) => {
      if (res) {
        const account: Account = res;
        this.localStorageService.store('activeCompany', account.companyId);

        // Only fetch unseen messages if technoUserId is valid
        if (res.technoUserId && !isNaN(Number(res.technoUserId))) {
          this.accountService.fetchUnseenMessages(Number(res.technoUserId)).subscribe();
        } else {
          console.warn('Invalid technoUserId:', res.technoUserId);
        }

        account !== null
          ? this.router.navigate(['home'])
          : this.router.navigate(['/account/login']);
      }
    });
    this.updateGreeting();
    this.greetingIntervalId = window.setInterval(() => this.updateGreeting(), 60 * 1000);
  }

  isAuthenticated(): boolean {
    return this.accountService.isAuthenticated();
  }

  login(): void {
    this.router.navigate(['/account/login']);
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    if (this.greetingIntervalId !== undefined) {
      window.clearInterval(this.greetingIntervalId);
    }
  }

  private updateGreeting(account: Account | null = this.account): void {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const isMorning = minutes >= 360 && minutes < 600;
    const isDay = minutes >= 600 && minutes < 1110;
    const key = isMorning
      ? 'home.greetingMorning'
      : isDay
        ? 'home.greetingDay'
        : 'home.greetingEvening';
    const fallback = isMorning ? 'Dobro jutro' : isDay ? 'Dobar dan' : 'Dobro vece';
    const translated = this.translateService.instant(key);
    const greeting = translated === key ? fallback : translated;
    const name = this.getDisplayName(account);
    this.greetingText = name ? `${greeting}, ${name}` : greeting;
  }

  private getDisplayName(account: Account | null): string {
    if (!account) {
      return '';
    }
    const parts = [account.firstName, account.lastName].filter(Boolean);
    if (parts.length) {
      return parts.join(' ').trim();
    }
    return account.username || account.login || '';
  }
}
